import React, { Suspense, useMemo } from "react";
import { Form, Col } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Await, useRouteLoaderData } from "react-router-dom";

export default function StationPicker({
    formData,
    setFormData,
    name,
    moduleFilterFunction = () => true,
    filterFunction = () => true,
    disabled,
    loadOnChange,
    includeCompetitors = false,
    multiple = false,
    label = "Station",
    placeholder = "Select station...",
    formKey = "station",
    colXl = 3,
    clearDemo = true,
    demoKey = "demo",
    clearDaypart = true,
    daypartKey = "daypart",
    size = "md",
}) {
    const { radios, competitorRadios, radiosModuleSettings } = useRouteLoaderData("root");

    const allPromise = useMemo(
        () => Promise.all([radios, includeCompetitors ? competitorRadios : [], radiosModuleSettings]),
        [radios, competitorRadios, includeCompetitors, radiosModuleSettings],
    );

    async function handleChange(value) {
        let formDataCopy = { ...formData, [formKey]: value };

        if (loadOnChange && value.length) {
            const savedFormData = await loadOnChange(value[0].radio_id);
            if (savedFormData) {
                formDataCopy = { ...formData, ...savedFormData, [formKey]: value };
            }
        }

        if (!value.length) {
            if (clearDemo) formDataCopy[demoKey] = [];

            if (clearDaypart) {
                formDataCopy[daypartKey] = [];
                formDataCopy[`${daypartKey}_days`] = [];
                formDataCopy[`${daypartKey}_hourRange`] = [];
            }
        }

        setFormData(formDataCopy);
    }

    return (
        <Suspense fallback={<Fallback name={name} colXl={colXl} label={label} formKey={formKey} />}>
            <Await resolve={allPromise}>
                {([resolvedRadios, resolvedCompetitorRadios = [], resolvedModuleSettings]) => {
                    let combinedRadios = resolvedRadios.data
                        .concat((resolvedCompetitorRadios.data || []).filter(x => !resolvedRadios.data.map(rr => rr.radio_id).includes(x.radio_id)))
                        .filter(filterFunction)
                        .sort((a, b) => (a.client === b.client ? a.radio_name.localeCompare(b.radio_name) : a.client ? -1 : 1));

                    if (resolvedModuleSettings?.data) {
                        const validIds = new Set(
                            resolvedModuleSettings.data.filter(module => moduleFilterFunction(module) && module.enabled === true).map(r => r.radio_id),
                        );
                        combinedRadios = combinedRadios.filter(radio => validIds.has(radio.radio_id));
                    }

                    return (
                        <Form.Group as={Col} className={`col-12 col-xl-${colXl} min-w-station`}>
                            {label && <Form.Label>{label}</Form.Label>}
                            <Typeahead
                                id={`${name}-${formKey}-selection`}
                                size={size}
                                options={combinedRadios}
                                labelKey={"radio_name"}
                                highlightOnlyResult
                                placeholder={placeholder}
                                clearButton={true}
                                paginate={false}
                                maxResults={0}
                                multiple={multiple}
                                onChange={handleChange}
                                selected={formData[formKey]}
                                disabled={disabled || false}
                            />
                        </Form.Group>
                    );
                }}
            </Await>
        </Suspense>
    );
}

function Fallback({ name, colXl, label, formKey }) {
    return (
        <Form.Group as={Col} className={`col-12 col-xl-${colXl} min-w-station`}>
            {label && <Form.Label>{label}</Form.Label>}
            <Typeahead id={`${name}-${formKey}-selection-fallback`} options={[]} placeholder="Loading stations..." disabled />
        </Form.Group>
    );
}
